/**
 * What the transfer panel draws.
 *
 * WHY A STORE AT ALL
 *   lib/transfer.ts reports a stream of immutable records and has no idea a
 *   screen exists. This file is the only place that remembers them, so the
 *   engine stays testable without a renderer and the interface has exactly one
 *   subscription to make.
 *
 * WHAT IT IS FOR
 *   Dropping 200 files in should look like what it is: a handful of real
 *   uploads and a great many instant skips. So the store keeps finished
 *   transfers around (you want to see that 197 files cost nothing), keeps them
 *   in start order, and answers "are we busy, and how far along" in one value
 *   that does not change identity unless the answer does.
 *
 * DATA DEFINITIONS
 *
 *   A TransfersState is { items, panelOpen }.
 *   Interpretation: `items` is every transfer this session has seen, newest
 *   first, both in-flight and recently finished, capped at RETAIN so a long
 *   session cannot grow without bound. `panelOpen` is whether the person is
 *   looking at them.
 *
 *   A TransferSummary is the fold of `items` into the one line a header shows.
 *   Interpretation: `percent` covers the whole list, counting a finished
 *   transfer as all of its bytes. That is what makes the bar MONOTONE: measure
 *   only the active ones and a file completing would remove its own progress
 *   from the numerator and send the bar backwards.
 */
import { create } from 'zustand'
import { maxSeq, type Change } from '@lattice/core'
import {
  disposeHashWorkers, downloadFiles, isTerminal, observeTransfers, uploadFiles,
  type DownloadOptions, type DownloadSummary, type Transfer, type TransferId,
  type UploadFile, type UploadSummary,
} from '../lib/transfer'
import { useWorkspace } from './workspace'
import { persist } from './sync'

/** How many finished transfers to keep. Enough to scroll back through a big drop. */
const RETAIN = 250

export interface TransfersState {
  readonly items: readonly Transfer[]
  readonly panelOpen: boolean
  /** Record a new state for one transfer, keeping list order stable. */
  readonly upsert: (transfer: Transfer) => void
  readonly setPanelOpen: (open: boolean) => void
  /** Forget everything that has stopped. */
  readonly clearFinished: () => void
  readonly dismiss: (id: TransferId) => void
  readonly reset: () => void
}

/**
 * The one record of every transfer this session has seen.
 * useTransfers : -> TransfersState   (a React hook, and a plain store)
 */
export const useTransfers = create<TransfersState>()((set) => ({
  items: [],
  panelOpen: false,

  upsert: transfer => set(state => {
    const index = state.items.findIndex(item => item.id === transfer.id)
    if (index >= 0) {
      // Replace in place: a progress tick must not make a row jump.
      const items = state.items.slice()
      items[index] = transfer
      return { items }
    }
    return { items: prune([transfer, ...state.items]) }
  }),

  setPanelOpen: panelOpen => set({ panelOpen }),
  clearFinished: () => set(state => ({ items: state.items.filter(item => !isTerminal(item.phase)) })),
  dismiss: id => set(state => ({ items: state.items.filter(item => item.id !== id) })),
  reset: () => set({ items: [], panelOpen: false }),
}))

/** Drop the oldest finished rows once the list is too long. prune : [Transfer] -> [Transfer] */
function prune(items: readonly Transfer[]): Transfer[] {
  if (items.length <= RETAIN) return [...items]
  const kept: Transfer[] = []
  // Walk newest to oldest, keeping everything live and the newest finished rows.
  for (const item of items) {
    if (kept.length < RETAIN || !isTerminal(item.phase)) kept.push(item)
  }
  return kept
}

/* ------------------------------------------------------------------ */
/* Cancellation                                                        */
/* ------------------------------------------------------------------ */

/**
 * id -> how to stop it.
 *
 * Kept outside the store because a function is not state: putting it in the
 * store would make every snapshot uncomparable and every render suspicious.
 */
const stoppers = new Map<TransferId, () => void>()

/** Stop one transfer. Safe to call on one that has already finished. */
export function cancelTransfer(id: TransferId): void {
  stoppers.get(id)?.()
}

/** Stop everything in flight. cancelAllTransfers : -> void */
export function cancelAllTransfers(): void {
  for (const stop of stoppers.values()) stop()
}

/**
 * Transfers that were cancelled by `forgetTransfers` and have not finished
 * unwinding yet.
 *
 * Interpretation: ids whose remaining records belong to a session that is
 * over, and must be ignored rather than stored. Each entry removes itself when
 * that transfer reports its last record, so this is empty except for the few
 * microtasks after a sign-out.
 */
const unwinding = new Set<TransferId>()

/**
 * Stop and forget everything. Call this on sign-out, beside `forgetReplica`:
 * one person's file names must not survive into the next person's session, and
 * an upload started under one identity must not continue under another.
 *
 * WHY THE `unwinding` SET
 *   Cancelling is asynchronous. Each transfer stopped here reports one final
 *   "cancelled" record — in a microtask, after this function has returned and
 *   the store has been cleared — and `upsert` would faithfully put it back.
 *   Signing out mid-upload therefore used to blank the tray and then repopulate
 *   it, a tick later, with the previous person's file paths. Verified against
 *   the real store before this was added.
 * forgetTransfers : -> void
 */
export function forgetTransfers(): void {
  for (const id of stoppers.keys()) unwinding.add(id)
  cancelAllTransfers()
  stoppers.clear()
  disposeHashWorkers()
  useTransfers.getState().reset()
}

/** Feed one record into the store and keep the cancel registry honest. */
function receive(transfer: Transfer, cancel: () => void): void {
  if (unwinding.has(transfer.id)) {
    if (isTerminal(transfer.phase)) unwinding.delete(transfer.id)
    return
  }
  if (isTerminal(transfer.phase)) stoppers.delete(transfer.id)
  else stoppers.set(transfer.id, cancel)
  useTransfers.getState().upsert(transfer)
}

/**
 * Listen to every transfer, however it was started.
 *
 * Registered here, at import, rather than from a component: a download begun
 * from the command palette must appear in the tray even if the tray has not
 * mounted yet, and an upload must not depend on a screen being on.
 */
observeTransfers({
  onTransfer: receive,
  onChanges: absorb,
  hasContent: sha => storedContent(useWorkspace.getState().changes).has(sha),
})

/* ------------------------------------------------------------------ */
/* Starting work                                                       */
/* ------------------------------------------------------------------ */

/**
 * Upload files into a project, showing progress in the panel.
 *
 * startUpload : String [UploadFile] {message?} -> Promise<UploadSummary>
 */
export async function startUpload(
  projectId: string,
  files: readonly UploadFile[],
  options: { message?: string; concurrency?: number } = {},
): Promise<UploadSummary> {
  if (files.length > 0) useTransfers.getState().setPanelOpen(true)

  // Reporting and the content oracle come from the observer registered above,
  // so this and a bare `uploadFiles` call behave identically.
  return uploadFiles(projectId, files, options)
}

/**
 * Save files to disk, showing progress in the panel.
 *
 * The project's name is passed as the archive label so a mixed selection is
 * saved as "LCSEM1-12-files.zip" rather than something anonymous.
 * startDownload : String [String] DownloadOptions -> Promise<DownloadSummary>
 */
export async function startDownload(
  projectId: string,
  paths: readonly string[],
  options: Omit<DownloadOptions, 'onTransfer'> = {},
): Promise<DownloadSummary> {
  if (paths.length > 1) useTransfers.getState().setPanelOpen(true)
  return downloadFiles(projectId, paths, { label: projectNameOf(projectId), ...options })
}

/**
 * Put freshly committed changes into the replica immediately.
 *
 * The sync long-poll would deliver these within tens of milliseconds anyway,
 * but "anyway" is not "already": the file must be in the tree by the time the
 * upload row turns green. Applying a delta is idempotent, so the copy that
 * arrives from the poll a moment later changes nothing.
 * absorb : [Change] -> void
 */
function absorb(changes: readonly Change[]): void {
  if (changes.length === 0) return
  useWorkspace.getState().applyDelta({ changes, comments: [], cursor: maxSeq(changes) })
  persist()
}

/** The project's display name, for naming an archive. */
function projectNameOf(projectId: string): string {
  return useWorkspace.getState().projects.find(project => project.id === projectId)?.name ?? projectId
}

/**
 * Every content hash the workspace is known to hold.
 *
 * WHY THIS IS SOUND
 *   A change can only be written after its bytes were confirmed in storage,
 *   and content-addressed blobs are immutable and never removed. So a hash
 *   mentioned anywhere in the log is, permanently, stored — which lets an
 *   upload of already-known content skip its round trip entirely. If the
 *   inference is ever wrong the commit is refused and the uploader falls back
 *   to sending the bytes, so the worst case costs one request.
 * storedContent : [Change] -> Set<String>
 */
const storedContent = memoOnce((changes: readonly Change[]): Set<string> => {
  const known = new Set<string>()
  for (const change of changes) {
    if (change.sha) known.add(change.sha)
    if (change.prevSha) known.add(change.prevSha)
  }
  return known
})

/* ------------------------------------------------------------------ */
/* The summary                                                         */
/* ------------------------------------------------------------------ */

export interface TransferSummary {
  /** Transfers still going. */
  readonly active: number
  readonly uploading: number
  readonly downloading: number
  readonly done: number
  /** Finished without moving a byte — the content was already stored. */
  readonly skipped: number
  readonly failed: number
  readonly cancelled: number
  /**
   * Bytes accounted for, and the total in play. A finished transfer counts as
   * all of its bytes however it finished — skipped content moved none of them,
   * but there is nothing left to wait for either.
   */
  readonly bytesDone: number
  readonly bytesTotal: number
  /** 0-100 across the whole list; 100 when nothing is active. */
  readonly percent: number
  readonly busy: boolean
}

const EMPTY_SUMMARY: TransferSummary = {
  active: 0, uploading: 0, downloading: 0, done: 0, skipped: 0, failed: 0, cancelled: 0,
  bytesDone: 0, bytesTotal: 0, percent: 100, busy: false,
}

/**
 * Fold the list into one header line.
 * summarize : [Transfer] -> TransferSummary
 */
export function summarize(items: readonly Transfer[]): TransferSummary {
  if (items.length === 0) return EMPTY_SUMMARY
  let active = 0, uploading = 0, downloading = 0
  let done = 0, skipped = 0, failed = 0, cancelled = 0
  let bytesDone = 0, bytesTotal = 0

  for (const item of items) {
    bytesTotal += item.totalBytes
    switch (item.phase) {
      case 'done': done++; bytesDone += item.totalBytes; continue
      case 'skipped': skipped++; bytesDone += item.totalBytes; continue
      case 'failed': failed++; bytesDone += item.totalBytes; continue
      case 'cancelled': cancelled++; bytesDone += item.totalBytes; continue
      default: break
    }
    active++
    if (item.direction === 'upload') uploading++
    else downloading++
    bytesDone += item.bytesDone
  }

  return {
    active, uploading, downloading, done, skipped, failed, cancelled,
    bytesDone, bytesTotal,
    // With no bytes in play (a queue of empty files) there is nothing to wait
    // for, so report finished rather than a bar stuck at zero.
    percent: bytesTotal === 0 ? 100 : Math.min(100, Math.round((bytesDone / bytesTotal) * 100)),
    busy: active > 0,
  }
}

/**
 * A one-slot memo keyed on object identity.
 *
 * `items` is replaced, never mutated, so identity is a correct cache key — and
 * returning the same object lets a React selector bail out of a render.
 * memoOnce : (A -> B) -> (A -> B)
 */
function memoOnce<A extends object, B>(f: (a: A) => B): (a: A) => B {
  let lastInput: A | undefined
  let lastOutput: B
  return (a: A): B => {
    if (a !== lastInput) { lastInput = a; lastOutput = f(a) }
    return lastOutput
  }
}

const summaryOf = memoOnce((items: readonly Transfer[]) => summarize(items))

/**
 * Subscribe to the summary alone.
 * Stable by identity, so a component using it re-renders only when the numbers
 * actually change — not on every progress tick of a file it is not showing.
 * useTransferSummary : -> TransferSummary
 */
export const useTransferSummary = (): TransferSummary => useTransfers(state => summaryOf(state.items))

/** Subscribe to the transfers still in flight, newest first. */
export const useActiveTransfers = (): readonly Transfer[] => useTransfers(state => activeOf(state.items))

const activeOf = memoOnce((items: readonly Transfer[]) => items.filter(item => !isTerminal(item.phase)))
